import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import './styles.css';

/**
 * ComplianceWidget Component
 * 
 * Renders compliance/zoning regulation data with:
 * - Tabular display with REGULATION, ELEMENT, METRIC, CONSTRAINT, STANDARD, STATUS columns
 * - Expandable regulation categories
 * - Compliance cards showing pass/fail status
 * - Applicability indicators
 * 
 * @param {Object} props
 * @param {Object} props.data - Response data containing CSV representation
 * @param {string} props.data.response - CSV formatted compliance data
 * @param {string} props.data.sheetTitle - Optional title for the data
 * @param {Function} props.onComplete - Callback when rendering is complete
 */
const ComplianceWidget = ({ data, onComplete }) => {
    const [groupedData, setGroupedData] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [overallCompliance, setOverallCompliance] = useState({ status: 'Pass', color: '#0BAA06' });

    useEffect(() => {
        if (data && data.response) {
            parseAndProcessData(data.response);
        }
    }, [data]);

    const parseAndProcessData = (csvData) => {
        try {
            Papa.parse(csvData, {
                header: false,
                complete: (results) => {
                    const rows = results.data;
                    const processed = convertDataToGroupedFormat(rows);
                    setGroupedData(processed);
                    calculateOverallCompliance(processed);
                    setIsLoading(false);
                    
                    if (onComplete) {
                        onComplete('Compliance data rendered');
                    }
                },
                error: (err) => {
                    console.error('Error parsing CSV:', err);
                    setError('Failed to parse compliance data');
                    setIsLoading(false);
                }
            });
        } catch (err) {
            console.error('Error processing compliance data:', err);
            setError('Failed to process compliance data');
            setIsLoading(false);
        }
    };

    const convertDataToGroupedFormat = (rows) => {
        const grouped = [];

        // Iterate over each row (skipping the header row)
        for (let i = 1; i < rows.length; i++) {
            const [category, element, metric, constraint, standard, status] = rows[i];

            // Skip empty rows or header rows
            if (!category || category === 'Zoning Regulations') continue;

            // Find or create category group
            let categoryGroup = grouped.find(group => group.category === category);
            if (!categoryGroup) {
                categoryGroup = {
                    category: category,
                    elements: []
                };
                grouped.push(categoryGroup);
            }

            // Add element to category
            categoryGroup.elements.push({
                element: element,
                metric: metric,
                constraint: constraint,
                standard: standard,
                status: status
            });
        }

        return grouped;
    };

    const calculateOverallCompliance = (grouped) => {
        let allCompliant = true;

        for (let group of grouped) {
            for (let element of group.elements) {
                if (element.status !== 'Compliant') {
                    allCompliant = false;
                    break;
                }
            }
            if (!allCompliant) break;
        }

        setOverallCompliance({
            status: allCompliant ? 'Pass' : 'Fail',
            color: allCompliant ? '#0BAA06' : 'red'
        });
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
    };

    const handleCardClick = (category) => {
        setSelectedCategory(category);
    };

    if (isLoading) {
        return <div className="archie-widget-loading">Loading compliance data...</div>;
    }

    if (error) {
        return <div className="archie-widget-error">{error}</div>;
    }

    const title = data.sheetTitle || 'Regulation Compliance';

    return (
        <div className="csv-data-outer">
            <h2 style={{ marginLeft: '20px' }}>{title}</h2>
            <div className="csv-table-div" style={{ maxHeight: '85vh' }}>
                <div className="compliance-wrapper">
                    {/* Dropdown for category selection */}
                    <div className="dropdown">
                        <label htmlFor="categoryDropdown">Category</label>
                        <select
                            id="categoryDropdown"
                            className="dropdown-select"
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                        >
                            <option value="all">All</option>
                            {groupedData.map((group, idx) => (
                                <option
                                    key={idx}
                                    value={group.category.toLowerCase().replace(/\s+|,|\(|\)/g, '-')}
                                >
                                    {group.category}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Compliance Cards */}
                    {selectedCategory === 'all' && (
                        <div className="compliance-container">
                            {groupedData.map((group, idx) => (
                                <div
                                    key={idx}
                                    className="compliance-card"
                                    data-category-card={group.category.toLowerCase().replace(/\s+|,|\(|\)/g, '-')}
                                    onClick={() => handleCardClick(group.category.toLowerCase().replace(/\s+|,|\(|\)/g, '-'))}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            handleCardClick(group.category.toLowerCase().replace(/\s+|,|\(|\)/g, '-'));
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <h2>{group.category}</h2>
                                    <p>
                                        <strong>Applicability:</strong>{' '}
                                        <span className="applicability">Applicable</span>
                                    </p>
                                    <p>
                                        <strong>Compliance:</strong>{' '}
                                        <span className="compliant">Pass</span>
                                    </p>
                                </div>
                            ))}
                            {/* Final compliance card */}
                            <div
                                className="final-compliance-card"
                                style={{ padding: 0, margin: '10px 0', textAlign: 'center' }}
                            >
                                <p
                                    style={{
                                        color: overallCompliance.color,
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        margin: 0
                                    }}
                                >
                                    {overallCompliance.status}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Category Details */}
                    {groupedData.map((group, groupIdx) => {
                        const categoryId = group.category.toLowerCase().replace(/\s+|,|\(|\)/g, '-');
                        const isSelected = selectedCategory === categoryId;

                        return (
                            <div
                                key={groupIdx}
                                className="category-details"
                                id={`category-${categoryId}`}
                                style={{ display: isSelected ? 'block' : 'none' }}
                            >
                                {group.elements.map((element, elemIdx) => (
                                    <div
                                        key={elemIdx}
                                        className="element-details"
                                    >
                                        <h3>{element.element}</h3>
                                        <p>
                                            <strong>Description:</strong> {element.constraint}
                                        </p>
                                        <p>{element.metric}</p>
                                        <label htmlFor="applicability-select">Applicability</label>
                                        <select id="applicability-select" style={{ display: 'block', marginBottom: '10px' }}>
                                            <option value="Applicable">Applicable</option>
                                            <option value="Not Applicable">Not Applicable</option>
                                        </select>
                                        <p>
                                            <strong>Compliance:</strong>{' '}
                                            <span
                                                style={{
                                                    color: element.status === 'Compliant' ? '#0BAA06' : 'red',
                                                    display: 'block'
                                                }}
                                            >
                                                {element.status === 'Compliant' ? 'Pass' : 'Fail'}
                                            </span>
                                        </p>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ComplianceWidget;
